// apps/patient/src/pages/classes/ClassBrowser.tsx

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, MapPin, Clock, Calendar, CheckCircle, Star } from 'lucide-react';
import { patientApiService } from '../../services/api';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const formatRecurrence = (pattern: any) => {
  if (!pattern?.days?.length) return '';
  return pattern.days.map((d: number) => DAYS[d]).join(', ');
};

export const ClassBrowser = () => {
  const navigate = useNavigate();
  const [classes, setClasses] = useState<any[]>([]);
  const [membershipDefIds, setMembershipDefIds] = useState<Set<string>>(new Set());
  const [enrolledDefIds, setEnrolledDefIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadClasses();
  }, []);

  const loadClasses = async () => {
    setLoading(true);
    try {
      const [data, memberships, enrollments] = await Promise.all([
        patientApiService.getAvailableClasses(),
        patientApiService.getMyClassMemberships().catch(() => []),
        patientApiService.getMyClassEnrollments().catch(() => []),
      ]);
      setClasses(data || []);
      setMembershipDefIds(new Set<string>(
        (memberships || [])
          .filter((m: any) => m.status === 'active')
          .map((m: any) => m.class_definition_id)
          .filter(Boolean)
      ));
      setEnrolledDefIds(new Set<string>(
        (enrollments || [])
          .filter((e: any) => ['enrolled', 'checked_in', 'waitlisted'].includes(e.status))
          .map((e: any) => e.class_definition?.id || e.class_session?.class_definition_id)
          .filter(Boolean)
      ));
    } catch (err) {
      console.error('Failed to load classes:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-6">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Classes</h1>

      {classes.length === 0 ? (
        <div className="text-center py-12">
          <Users className="mx-auto h-12 w-12 text-gray-300" />
          <p className="mt-2 text-gray-500">No classes available at this time.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {classes.map((cls: any) => {
            const isMember = membershipDefIds.has(cls.id);
            const isEnrolled = enrolledDefIds.has(cls.id);
            return (
            <div
              key={cls.id}
              onClick={() => navigate(`/classes/${cls.id}`)}
              className="bg-white rounded-lg shadow-sm border border-gray-200 p-5 hover:shadow-md cursor-pointer transition-shadow"
            >
              <div className="flex items-start justify-between gap-2 mb-2">
                <h3 className="text-lg font-semibold text-gray-900">{cls.name}</h3>
                {isMember ? (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700 flex-shrink-0">
                    <Star className="w-3 h-3" />
                    Member
                  </span>
                ) : isEnrolled ? (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700 flex-shrink-0">
                    <CheckCircle className="w-3 h-3" />
                    Enrolled
                  </span>
                ) : null}
              </div>
              {cls.description && (
                <p className="text-sm text-gray-600 mb-3 line-clamp-2">{cls.description}</p>
              )}
              <div className="space-y-1.5 text-sm text-gray-500">
                {cls.provider && (
                  <div className="flex items-center gap-2">
                    <Users className="w-3.5 h-3.5" />
                    <span>{cls.provider.first_name} {cls.provider.last_name}</span>
                  </div>
                )}
                {cls.location && (
                  <div className="flex items-center gap-2">
                    <MapPin className="w-3.5 h-3.5" />
                    <span>{cls.location.name}</span>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <Calendar className="w-3.5 h-3.5" />
                  <span>{formatRecurrence(cls.recurrence_pattern)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-3.5 h-3.5" />
                  <span>{cls.start_time} - {cls.end_time} ({cls.duration_minutes} min)</span>
                </div>
              </div>
              <div className="mt-3 flex items-center justify-between">
                <span className="text-xs text-gray-400">Capacity: {cls.capacity}</span>
                {cls.allowed_payment_types?.includes('single_purchase') && (cls.single_purchase_price > 0 || cls.series_price > 0) && (
                  <span className="text-sm font-medium text-primary text-right">
                    {cls.single_purchase_price > 0 && <span>${Number(cls.single_purchase_price).toFixed(2)}/session</span>}
                    {cls.single_purchase_price > 0 && cls.series_price > 0 && <span className="text-gray-400"> | </span>}
                    {cls.series_price > 0 && <span>Series ${Number(cls.series_price).toFixed(2)}</span>}
                  </span>
                )}
              </div>
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ClassBrowser;
